import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthApiError } from '@supabase/supabase-js';
import { useSupabaseClient } from '../lib/supabaseClient';
import { useAuthContext } from './AuthContext';
import logDev from '../utils/logDev';
import logError from '../utils/logError';
import { camelizeKeys } from "../utils/camelize";
import { decamelizeKeys } from "../utils/decamelize";
import { uploadFile, deleteFile } from '../services/publitio';

const DataContext = createContext();

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const findNestedValue = (data, key) => {
  if (data === null || data === undefined) {
    return undefined;
  }

  if (Array.isArray(data)) {
    for (const item of data) {
      const result = findNestedValue(item, key);
      if (result !== undefined) {
        return result;
      }
    }
    return undefined;
  }

  if (!isPlainObject(data)) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(data, key)) {
    return data[key];
  }

  for (const value of Object.values(data)) {
    const result = findNestedValue(value, key);
    if (result !== undefined) {
      return result;
    }
  }

  return undefined;
};

export const computeName = (firstName, middleName, lastName) => {
  const parts = [firstName, middleName, lastName]
    .map((part) => {
      if (part === null || part === undefined) {
        return '';
      }
      const value = String(part).trim();
      return value;
    })
    .filter((part) => part.length > 0);

  return parts.join(' ');
};

const sanitizeNamePart = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  const trimmed = String(value).trim();
  return trimmed;
};

const toSnakeKey = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const cloneStructuredFields = (value) => {
  if (!isPlainObject(value)) {
    return {};
  }

  return { ...value };
};

const applyLegacyNameFromStructuredFields = (payload) => {
  if (!isPlainObject(payload)) {
    return;
  }

  const structuredSource = payload.structuredFields ?? payload.structured_fields;
  const structured = cloneStructuredFields(structuredSource);

  const firstName = sanitizeNamePart(
    payload.firstName ?? payload.first_name ?? structured.firstName ?? structured.first_name
  );
  const middleName = sanitizeNamePart(
    payload.middleName ?? payload.middle_name ?? structured.middleName ?? structured.middle_name
  );
  const lastName = sanitizeNamePart(
    payload.lastName ?? payload.last_name ?? structured.lastName ?? structured.last_name
  );

  const fallbackFullName = sanitizeNamePart(
    structured.fullName ?? structured.full_name ?? structured.name ?? payload.name
  );

  const existingName = sanitizeNamePart(payload.name);
  const computedFullName = computeName(firstName, middleName, lastName);
  const finalFullName = computedFullName || existingName || fallbackFullName;

  if (finalFullName) {
    payload.name = finalFullName;
  } else if (payload.name !== undefined) {
    payload.name = existingName;
  }

  const setOrDelete = (key, value) => {
    const snakeKey = toSnakeKey(key);
    if (value) {
      structured[key] = value;
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    } else {
      delete structured[key];
      if (snakeKey !== key) {
        delete structured[snakeKey];
      }
    }
  };

  const shouldProcessStructuredFields =
    firstName ||
    middleName ||
    lastName ||
    finalFullName ||
    Object.keys(structured).length > 0;

  if (shouldProcessStructuredFields) {
    setOrDelete('firstName', firstName);
    setOrDelete('middleName', middleName);
    setOrDelete('lastName', lastName);
    if (finalFullName) {
      setOrDelete('fullName', finalFullName);
      setOrDelete('name', finalFullName);
    } else {
      setOrDelete('fullName', null);
      setOrDelete('name', null);
    }

    if (Object.keys(structured).length > 0) {
      payload.structuredFields = structured;
    } else {
      delete payload.structuredFields;
    }
  }

  delete payload.firstName;
  delete payload.middleName;
  delete payload.lastName;
  delete payload.first_name;
  delete payload.middle_name;
  delete payload.last_name;
  delete payload.structured_fields;
};

export const prepareClientData = (rawClient = {}) => {
  if (!isPlainObject(rawClient)) {
    return decamelizeKeys(rawClient);
  }

  const { structuredFields, ...rest } = rawClient;
  const prepared = { ...rest };

  if (isPlainObject(structuredFields)) {
    Object.entries(structuredFields).forEach(([key, value]) => {
      if (prepared[key] === undefined) {
        prepared[key] = value;
      }
    });
  }

  if (structuredFields !== undefined) {
    prepared.structuredFields = structuredFields;
  }

  const valueFromSources = (field) => {
    if (rawClient[field] !== undefined) {
      return rawClient[field];
    }
    if (prepared[field] !== undefined) {
      return prepared[field];
    }
    return findNestedValue(structuredFields, field);
  };

  ['firstName', 'middleName', 'lastName', 'company', 'position', 'policyType'].forEach((field) => {
    const value = valueFromSources(field);
    if (value !== undefined) {
      prepared[field] = value;
    }
  });

  prepared.name = computeName(
    valueFromSources('firstName'),
    valueFromSources('middleName'),
    valueFromSources('lastName')
  );

  return decamelizeKeys(prepared);
};

// Export the custom hook
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user, signOut } = useAuthContext();
  const supabase = useSupabaseClient();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleAuthFailure = async (error) => {
    if (error instanceof AuthApiError || error?.status === 401) {
      try {
        await supabase.auth.refreshSession();
      } catch {
        try {
          await signOut();
        } finally {
          window.location.href = '/login';
        }
      }
    }
  };

  logDev('DataProvider rendering, user:', user?.id);

  // Load data when user changes
  useEffect(() => {
    if (user && supabase) {
      fetchData();
    }
  }, [supabase, user?.id, user?.supabaseId, user?.role]);

  const getAuthId = () => user?.supabaseId || user?.authId || user?.id;

  const fetchData = async () => {
    if (!supabase) {
      logError('fetchData', new Error('Supabase client not available'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      logDev('Fetching data from Supabase...');

      let clientsData = [];
      let usersData = [];
      let proposalsData = [];
      let documentsData = [];

      // Resolve profile and agent tracker IDs
      const authId = getAuthId();
      const { data: userProfile, error: userProfileError } = await supabase
        .from('users_pf')
        .select('*')
        .eq('auth_id', authId)
        .single();

      if (userProfileError) {
        throw userProfileError;
      }

      const profileId = userProfile?.id;

      let agentTrackerId = null;
      if (user.role !== 'client') {
        const { data: agentProfile, error: agentError } = await supabase
          .from('agent_tracker')
          .select('id')
          .eq('user_id', profileId)
          .maybeSingle();

        if (agentError) throw agentError;
        agentTrackerId = agentProfile?.id ?? null;
      }

      if (user.role === 'client') {
        // Clients only need their own record and related advisor data
        const { data: client, error: clientError } = await supabase
          .from('clients_pf')
          .select('*, spouse_info, children_info')
          .eq('id', profileId)
          .maybeSingle();
        if (clientError) throw clientError;

        const advisorId = client?.advisor_id || null;

        let userQuery = supabase
          .from('users_pf')
          .select('*');
        if (advisorId === null) {
          userQuery = userQuery.is('advisor_id', null);
        } else {
          userQuery = userQuery.eq('advisor_id', advisorId);
        }
        const { data: uData, error: uError } = await userQuery;
        if (uError) throw uError;

        let proposalQuery = supabase
          .from('projections_pf')
          .select('*');
        if (advisorId === null) {
          proposalQuery = proposalQuery.is('advisor_id', null);
        } else {
          proposalQuery = proposalQuery.eq('advisor_id', advisorId);
        }
        const { data: pData, error: pError } = await proposalQuery;
        if (pError) throw pError;
        let documentsQuery = supabase
          .from('documents_pf')
          .select('*')
          .or(`client_id.eq.${profileId},user_id.eq.${profileId}`);
        const { data: dData, error: dError } = await documentsQuery;
        if (dError) throw dError;

        clientsData = client ? [client] : [];
        usersData = uData || [];
        proposalsData = pData || [];
        documentsData = dData || [];

        clientsData = clientsData.map(camelizeKeys);
        usersData = usersData.map(camelizeKeys);
        proposalsData = proposalsData.map(camelizeKeys);
        documentsData = documentsData.map(camelizeKeys);
      } else {
        // Advisors/managers fetch by advisor id, admins fetch all
        let clientsQuery = supabase
          .from('clients_pf')
          .select('*, spouse_info, children_info');
        let usersQuery = supabase
          .from('users_pf')
          .select('*');
        let proposalsQuery = supabase
          .from('projections_pf')
          .select('*');
        let documentsQuery = supabase
          .from('documents_pf')
          .select('*');

        if (user.role !== 'admin') {
          if (!agentTrackerId) {
            throw new Error('Agent tracker profile not found for current user');
          }
          clientsQuery = clientsQuery.eq('advisor_id', agentTrackerId);
          usersQuery = usersQuery.eq('advisor_id', agentTrackerId);
          proposalsQuery = proposalsQuery.eq('advisor_id', agentTrackerId);
          documentsQuery = documentsQuery.eq('user_id', profileId);
        }

        const { data: cData, error: cError } = await clientsQuery;
        if (cError) throw cError;

        const { data: uData, error: uError } = await usersQuery;
        if (uError) throw uError;

        const { data: pData, error: pError } = await proposalsQuery;
        if (pError) throw pError;
        const { data: dData, error: dError } = await documentsQuery;
        if (dError) throw dError;

        clientsData = (cData || []).map(camelizeKeys);
        usersData = (uData || []).map(camelizeKeys);
        proposalsData = (pData || []).map(camelizeKeys);
        documentsData = (dData || []).map(camelizeKeys);
      }

      logDev('Data fetched successfully:', {
        clients: clientsData.length,
        users: usersData.length,
        proposals: proposalsData.length,
        documents: documentsData.length
      });

      setClients(clientsData);
      setUsers(usersData);
      setProposals(proposalsData);
      setDocuments(documentsData);
    } catch (err) {
      await handleAuthFailure(err);
      logError('fetchData', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // CRUD operations for clients
  const addClient = async (client) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      // Strip fields that shouldn't be persisted and the ID before inserting
      const {
        id, // ALWAYS exclude id when inserting - let DB generate it
        status_history,
        last_activity,
        ...cleanClient
      } = client;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Resolve profile and agent tracker IDs
      const authId = getAuthId();
      const { data: userProfile, error: userProfileError } = await supabase
        .from('users_pf')
        .select('id')
        .eq('auth_id', authId)
        .single();

      if (userProfileError) {
        logError('users_pf profile lookup', userProfileError);
        throw userProfileError;
      }

      const profileId = userProfile?.id;
      if (!profileId) {
        throw new Error('User profile not found');
      }

      const { data: agentProfile, error: agentError } = await supabase
        .from('agent_tracker')
        .select('id')
        .eq('user_id', profileId)
        .maybeSingle();

      if (agentError) {
        logError('agent_tracker lookup', agentError);
        throw agentError;
      }

      const advisorId = agentProfile?.id;
      if (!advisorId) {
        throw new Error('Agent tracker profile not found for current user');
      }

      const preparedClient = prepareClientData({
        ...cleanClient,
        advisorId,
        createdBy: profileId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      const { data, error } = await supabase
        .from('clients_pf')
        .insert(preparedClient)
        .select()
        .single();

      if (error) {
        logError('clients_pf insert', error);
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from insert operation');
      }

      setClients(prev => [...prev, camelizeKeys(data)]);

      return camelizeKeys(data);
    } catch (error) {
      await handleAuthFailure(error);
      logError('clients_pf add', error);
      throw error;
    }
  };

  const updateClient = async (id, updates) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      // Remove the ID and fields that shouldn't be updated from the payload
      const {
        id: removeId, // Never update the ID
        status_history,
        last_activity,
        created_at, // Don't update created_at
        ...cleanUpdates
      } = updates;

      // Add updated_at timestamp
      cleanUpdates.updatedAt = new Date().toISOString();

      const preparedUpdates = prepareClientData(cleanUpdates);

      const { data, error } = await supabase
        .from('clients_pf')
        .update(preparedUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const camel = camelizeKeys(data);
        setClients(prev => prev.map(client =>
          client.id === id ? { ...client, ...camel } : client
        ));
        return camel;
      }

      return null;
    } catch (error) {
      await handleAuthFailure(error);
      logError('clients_pf update', error);
      throw error;
    }
  };

  const deleteClient = async (id) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const { error } = await supabase
        .from('clients_pf')
        .delete()
        .eq('id', id);
      
      if (error) throw error;

      setClients(prev => prev.filter(client => client.id !== id));
    } catch (error) {
      await handleAuthFailure(error);
      logError('clients_pf delete', error);
      throw error;
    }
  };

  // CRUD operations for users
  const addUser = async (userData) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const { id, profileImageId, profileImageUrl, ...cleanUserData } = userData;
      applyLegacyNameFromStructuredFields(cleanUserData);
      if (profileImageId || profileImageUrl) {
        cleanUserData.avatar = null;
        cleanUserData.profileImageId = profileImageId;
        cleanUserData.profileImageUrl = profileImageUrl;
      }
      const userId = user.supabaseId || user.id;

      const { data, error } = await supabase
        .from('users_pf')
        .insert(
          decamelizeKeys({
            ...cleanUserData,
            advisorId: userId,
            createdAt: new Date().toISOString()
          })
        )
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const camel = camelizeKeys(data);
        setUsers(prev => [...prev, camel]);
        return camel;
      }
      return null;
    } catch (error) {
      await handleAuthFailure(error);
      logError('users_pf add', error);
      throw error;
    }
  };

  const updateUser = async (id, updates) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      // Remove id and created_at from updates
      const { id: removeId, created_at, profileImageId, profileImageUrl, ...cleanUpdates } = updates;
      applyLegacyNameFromStructuredFields(cleanUpdates);
      if (profileImageId || profileImageUrl) {
        cleanUpdates.avatar = null;
        cleanUpdates.profileImageId = profileImageId;
        cleanUpdates.profileImageUrl = profileImageUrl;
      }
      cleanUpdates.updatedAt = new Date().toISOString();

      const numericUserFields = ['advisorId', 'teamId', 'agentCode'];
      numericUserFields.forEach((field) => {
        const camelValue = cleanUpdates[field];
        if (typeof camelValue === 'string' && camelValue.trim() === '') {
          cleanUpdates[field] = null;
        }

        const snakeField = toSnakeKey(field);
        const snakeValue = cleanUpdates[snakeField];
        if (typeof snakeValue === 'string' && snakeValue.trim() === '') {
          cleanUpdates[snakeField] = null;
        }
      });

      const { data, error } = await supabase
        .from('users_pf')
        .update(decamelizeKeys(cleanUpdates))
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const camel = camelizeKeys(data);
        setUsers(prev => prev.map(user =>
          user.id === id ? { ...user, ...camel } : user
        ));
        return camel;
      }
      return null;
    } catch (error) {
      await handleAuthFailure(error);
      logError('users_pf update', error);
      throw error;
    }
  };

  const deleteUser = async (id) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const { error } = await supabase
        .from('users_pf')
        .delete()
        .eq('id', id);
      
      if (error) throw error;

      setUsers(prev => prev.filter(user => user.id !== id));
    } catch (error) {
      await handleAuthFailure(error);
      logError('users_pf delete', error);
      throw error;
    }
  };

  // CRUD operations for proposals
  const addProposal = async (proposal) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const { id, ...cleanProposal } = proposal;
      const userId = user.supabaseId || user.id;

      const { data, error } = await supabase
        .from('projections_pf')
        .insert(
          decamelizeKeys({
            ...cleanProposal,
            advisorId: userId,
            createdAt: new Date().toISOString()
          })
        )
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const camel = camelizeKeys(data);
        setProposals(prev => [...prev, camel]);
        return camel;
      }
      return null;
    } catch (error) {
      await handleAuthFailure(error);
      logError('projections_pf add', error);
      throw error;
    }
  };

  const updateProposal = async (id, updates) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const { id: removeId, created_at, ...cleanUpdates } = updates;
      cleanUpdates.updatedAt = new Date().toISOString();

      const { data, error } = await supabase
        .from('projections_pf')
        .update(decamelizeKeys(cleanUpdates))
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const camel = camelizeKeys(data);
        setProposals(prev => prev.map(proposal =>
          proposal.id === id ? { ...proposal, ...camel } : proposal
        ));
        return camel;
      }
      return null;
    } catch (error) {
      await handleAuthFailure(error);
      logError('projections_pf update', error);
      throw error;
    }
  };

  const deleteProposal = async (id) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const { error } = await supabase
        .from('projections_pf')
        .delete()
        .eq('id', id);
      
      if (error) throw error;

      setProposals(prev => prev.filter(proposal => proposal.id !== id));
    } catch (error) {
      await handleAuthFailure(error);
      logError('projections_pf delete', error);
      throw error;
    }
  };

  const fetchDocuments = async () => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const userId = user.supabaseId || user.id;
      let query = supabase
        .from('documents_pf')
        .select('*');

      if (user.role === 'client') {
        query = query.or(`client_id.eq.${userId},user_id.eq.${userId}`);
      } else if (user.role !== 'admin') {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const docs = (data || []).map(camelizeKeys);
      setDocuments(docs);
      return docs;
    } catch (error) {
      await handleAuthFailure(error);
      logError('documents_pf fetch', error);
      throw error;
    }
  };

  const addDocument = async ({ file, name, clientId, publitioId, url }) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    if (!file && !(publitioId && url)) {
      throw new Error('File or publitio data required');
    }

    try {
      let upload = null;
      if (file) {
        upload = await uploadFile(file, 'documents');
      }

      const userId = user.supabaseId || user.id;
      const { data, error } = await supabase
        .from('documents_pf')
        .insert(
          decamelizeKeys({
            userId,
            clientId,
            name: name || file?.name,
            publitioId: publitioId || upload.public_id,
            url: url || upload.url,
            createdAt: new Date().toISOString(),
          })
        )
        .select()
        .single();

      if (error) throw error;

      const doc = camelizeKeys(data);
      setDocuments((prev) => [...prev, doc]);
      return doc;
    } catch (error) {
      await handleAuthFailure(error);
      logError('documents_pf add', error);
      throw error;
    }
  };

  const recordDocument = async ({ name, clientId, publitioId, url }) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }
    try {
      const userId = user.supabaseId || user.id;
      const { data, error } = await supabase
        .from('documents_pf')
        .insert(
          decamelizeKeys({
            userId,
            clientId,
            name,
            publitioId,
            url,
            createdAt: new Date().toISOString(),
          })
        )
        .select()
        .single();

      if (error) throw error;

      const doc = camelizeKeys(data);
      setDocuments((prev) => [...prev, doc]);
      return doc;
    } catch (error) {
      await handleAuthFailure(error);
      logError('documents_pf record', error);
      throw error;
    }
  };

  const deleteDocument = async (id) => {
    if (!supabase) {
      throw new Error('Supabase client not available');
    }

    try {
      const doc = documents.find((d) => d.id === id);
      if (doc?.publitioId) {
        try {
          await deleteFile(doc.publitioId);
        } catch (e) {
          await handleAuthFailure(e);
          logError('Publitio delete', e);
        }
      }

      const { error } = await supabase
        .from('documents_pf')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (error) {
      await handleAuthFailure(error);
      logError('documents_pf delete', error);
      throw error;
    }
  };

  // Refresh data
  const refreshData = async () => {
    if (supabase) {
      await fetchData();
    }
  };

  const value = {
    clients,
    users,
    proposals,
    documents,
    loading,
    error,
    addClient,
    updateClient,
    deleteClient,
    addUser,
    updateUser,
    deleteUser,
    addProposal,
    updateProposal,
    deleteProposal,
    fetchDocuments,
    addDocument,
    recordDocument,
    deleteDocument,
    refreshData
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
